import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import axios from "axios";
import { TimeStamp, BackToTopButton, Clock } from "../Components";
import Loading from "../Data/Assets/Images/filler.gif";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Scheduler = () => {
  const futureCycles = 140;
  const format = "ddd, MMM D, YYYY h:mm A";
  const rotationDuration = 150 * 60 * 1000;

  const [timetable, setTimetable] = useState([]);
  const [timeLeftToNextDay, setTimeLeftToNextDay] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [addedTimestamps, setAddedTimestamps] = useState([]);
  const [selectedTimestamps, setSelectedTimestamps] = useState([]);
  const [nextExpiryTime, setNextExpiryTime] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const generateFutureCycles = useCallback(
    (initialExpiry) => {
      let nextMomentMil = initialExpiry;
      const cycles = [];

      for (let i = 0; i < futureCycles; i++) {
        if (i !== 0) {
          nextMomentMil += rotationDuration;
        }
        const nextMoment = moment(nextMomentMil);
        cycles.push({
          time: nextMoment.format(format),
        });
      }

      setTimetable(cycles);
      setNextExpiryTime(initialExpiry);
      setIsLoading(false);
      setIsRefreshing(false);
    },
    [futureCycles, format, rotationDuration]
  );

  const toggleTimestamp = (time) => {
    setAddedTimestamps((prev) => {
      const updatedList = prev.includes(time)
        ? prev.filter((t) => t !== time)
        : [...prev, time];

      return updatedList.sort((a, b) => moment(a, format) - moment(b, format));
    });

    setSelectedTimestamps((prev) => {
      const updatedList = prev.includes(time)
        ? prev.filter((t) => t !== time)
        : [...prev, time];

      return updatedList.sort((a, b) => moment(a, format) - moment(b, format));
    });
  };

  const fetchCetusData = useCallback(() => {
    axios
      .get("https://oracle.browse.wf/worldState.json")
      .then((response) => {
        const data = response.data;

        const cetusEvent = data.SyndicateMissions?.find(
          (mission) => mission.Tag === "CetusSyndicate"
        );

        if (cetusEvent) {
          const expiryTimestamp = cetusEvent.Expiry.$date.$numberLong;
          const expiryTime = parseInt(expiryTimestamp);

          if (expiryTime > Date.now()) {
            generateFutureCycles(expiryTime);
          }
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setIsRefreshing(false);
      });
  }, [generateFutureCycles]);

  useEffect(() => {
    setIsLoading(true);
    fetchCetusData();
  }, [fetchCetusData]);

  const updateTime = useCallback(() => {
    if (nextExpiryTime) {
      const nextRotationMoment = moment(nextExpiryTime);
      const timeDifference = nextRotationMoment.diff(moment());

      if (timeDifference <= 0) {
        if (!isRefreshing) {
          setIsRefreshing(true);
          fetchCetusData();
        }
      } else {
        const countdown = moment.duration(timeDifference);
        setTimeLeftToNextDay(
          `${countdown.hours() > 0 ? countdown.hours() + "h" : ""} ${
            countdown.minutes() >= 0 ? countdown.minutes() + "m" : ""
          }`.trim()
        );
      }
    }
  }, [nextExpiryTime, fetchCetusData, isRefreshing]);

  useEffect(() => {
    const interval = setInterval(() => {
      updateTime();
    }, 1000);

    return () => clearInterval(interval);
  }, [updateTime]);

  useEffect(() => {
    if (isRefreshing) {
      const retryInterval = setInterval(() => {
        fetchCetusData();
      }, 10000);

      return () => clearInterval(retryInterval);
    }
  }, [isRefreshing, fetchCetusData]);

  const LoadingComponent = () => (
    <div className="loading">
      <img
        src={Loading}
        alt="loading"
        className="rounded-lg border-white border-1 w-full"
      />
    </div>
  );

  const resetTimestamps = () => {
    setSelectedTimestamps([]);
    setAddedTimestamps([]);
  };

  return (
    <div className="w-full flex flex-col justify-center items-center gap-5">
      <h1 className="font-pathway tracking-[1px] text-3xl lg:text-4xl font-bold shadow-lg">
        BOUNTY SCHEDULE
      </h1>
      <div className="font-lexend tracking-wider w-full md:w-3/4 lg:w-1/2 flex flex-col justify-start items-center gap-4 bg-zinc-800/60 h-full p-2 lg:p-4 rounded">
        <Clock />
        <div className="flex flex-col gap-4 w-fit">
          <div className="bg-white text-[#111] border-2 border-zinc-600/50 p-2">
            <h1 className=" text-center text-xl font-bold lg:text-[20px]">
              NEXT ROTATION IN {timeLeftToNextDay || "0m"}
            </h1>
          </div>
          <TimeStamp
            timestamps={selectedTimestamps}
            setTimestamps={resetTimestamps}
          />
        </div>
        <p className="font-bold text-zinc-400/90">UPCOMING CYCLE RESETS</p>
        {isLoading ? (
          <LoadingComponent />
        ) : (
          <div className="w-full flex flex-col justify-center items-center gap-4 font-semibold">
            <div className="flex flex-col w-full gap-3">
              {timetable.map(({ time }, index) => {
                return (
                  <div
                    key={index}
                    className="flex justify-between gap-2 items-center px-5 py-2 w-full border-2 border-zinc-600/50"
                  >
                    <p className="font-bold text-xl">
                      {moment(time, format).format("D MMM")}
                    </p>

                    <p className="font-bold text-xl">
                      {moment(time, format).format("h:mm A ")}
                    </p>

                    <button
                      onClick={() => toggleTimestamp(time)}
                      className={`border-2 border-zinc-600/50 text-lg w-10 h-10 flex justify-center items-center p-2 rounded-full transition-all duration-400 ease-[cubic-bezier(0.4, 0, 0.2, 1)] ${
                        addedTimestamps.includes(time)
                          ? "bg-primary text-white"
                          : "bg-white text-[#111]"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faPlus}
                        className={`text-lg block transition-transform duration-400 ease-[cubic-bezier(0.4, 0, 0.2, 1)]`}
                        style={{
                          transform: addedTimestamps.includes(time)
                            ? "rotate(-315deg)"
                            : "rotate(0deg)",
                        }}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <BackToTopButton />
      </div>
    </div>
  );
};

export default Scheduler;
